/**
 * VoiceAgentQuickSwitcher — Voice Agent quick switcher in the left sidebar.
 *
 * 1.166.1: ONE ROW PER TAB. Prior dedup by `${name}::${voice}` collapsed
 * two same-name same-voice tabs into one row, which violated the user's
 * mental model ("I have 2 Steve-O tabs, I want to see 2 Steve-O rows").
 *
 * Each row has two click targets:
 *   - Name button (left): navigate to THAT tab via setActiveTab.
 *   - Settings icon (right): navigate AND open the voice settings modal.
 *
 * Tab identity (not name) is the primary key. Tab label rendered as
 * subtle secondary text so two tabs with the same agent name remain
 * distinguishable visually.
 *
 * Empty state when tabs.length === 0 OR no tenantId: render header only.
 * Active highlight: row whose tab.id === activeTabId gets bg-vibe-accent/10.
 *
 * Testids:
 *  - Row name button: `voice-agent-quick-switcher-button-${voiceAgentName}`
 *  - Row settings icon: `voice-agent-quick-switcher-settings-${voiceAgentName}`
 * Two rows with the same name share the same testid; RTL's queryAllByTestId
 * handles duplicates. Tab.id is on the wrapper div as
 * `data-tab-id="${tab.id}"` for unambiguous test anchoring.
 */
import { useVoiceTabsStore } from '@/stores/voiceTabsStore';
import { useVoiceStore } from '@/stores/voiceStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useUIStore } from '@/stores/uiStore';
import { VoiceSettingsIcon } from '../voice/VoiceSettingsIcon';

interface AgentEntry {
  tabId: string;
  tabLabel: string;
  voiceAgentName: string;
}

export function VoiceAgentQuickSwitcher() {
  const tabs = useVoiceTabsStore((s) => s.tabs);
  const activeTabId = useVoiceTabsStore((s) => s.activeTabId);
  const byTabId = useVoiceStore((s) => s.byTabId);
  const tenantId = useTenantStore((s) => s.tenantId);

  const agents: AgentEntry[] = (() => {
    if (tabs.length === 0 || !tenantId) return [];
    const out: AgentEntry[] = [];
    for (const tab of tabs) {
      const settings = byTabId[tab.id]?.settings;
      if (!settings) continue;
      const name = settings.voiceAgentName;
      if (!name) continue;
      out.push({ tabId: tab.id, tabLabel: tab.label, voiceAgentName: name });
    }
    return out;
  })();

  const switchToTab = (agent: AgentEntry) => {
    if (!tenantId) return;
    useVoiceTabsStore.getState().setActiveTab(tenantId, agent.tabId);
  };

  const handleNameClick = (agent: AgentEntry) => {
    switchToTab(agent);
  };

  const handleSettingsClick = (agent: AgentEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    switchToTab(agent);
    useUIStore.getState().setVoiceSettingsOpen(true);
  };

  return (
    <div className="border-t border-vibe-border p-3">
      <div className="text-xs text-vibe-text-secondary uppercase mb-2">
        Voice Agents
      </div>
      {agents.length > 0 && (
        <div className="space-y-1">
          {agents.map((agent) => {
            const isActive = agent.tabId === activeTabId;
            return (
              <div
                key={agent.tabId}
                data-tab-id={agent.tabId}
                className={`flex items-center rounded ${
                  isActive ? 'bg-vibe-accent/10' : 'hover:bg-vibe-bg-tertiary'
                }`}
              >
                <button
                  type="button"
                  data-testid={`voice-agent-quick-switcher-button-${agent.voiceAgentName}`}
                  onClick={() => handleNameClick(agent)}
                  className="flex-1 text-left px-2 py-1.5 text-sm text-vibe-text truncate"
                  title={`${agent.voiceAgentName} (${agent.tabLabel})`}
                >
                  {agent.voiceAgentName}
                  <span className="ml-2 text-xs text-vibe-text-secondary">
                    {agent.tabLabel}
                  </span>
                </button>
                <button
                  type="button"
                  data-testid={`voice-agent-quick-switcher-settings-${agent.voiceAgentName}`}
                  aria-label={`open voice settings for ${agent.voiceAgentName} (${agent.tabLabel})`}
                  onClick={(e) => handleSettingsClick(agent, e)}
                  className="px-2 py-1.5 text-vibe-text-secondary hover:text-vibe-text"
                >
                  <VoiceSettingsIcon className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
